import { useEffect, useMemo, useRef, useState } from "react";
import type { SoundRole } from "@desk/core";
import { effectiveSound } from "@desk/core";
import type { DeskSession } from "../hooks/useDeskSession";
import { nextOpId, nextSfxInstanceId } from "../lib/edit-helpers";
import { packAudioAssets, packEndTick } from "../lib/caption-preview";
import { playableAudioUrl, WARM_AUDIO_ASSET_ID } from "../lib/assets";
import {
  AudioAuditionController,
  prefersReducedMotion,
} from "../lib/audio-audition.js";
import { coachLineFor, dockLabelFor } from "./tools.js";
import { CreativeOptionCard } from "./CreativeOptionCard.js";
import { ToolCartButton } from "./ToolCartButton.js";
import { useStudioStore } from "./StudioStoreContext.js";
import {
  isStingLikeAsset,
  MUSIC_MOOD_DEFS,
  musicMoodFromResolved,
  type MusicMoodId,
} from "../lib/tool-selection.js";

const BASIC_ROLES: SoundRole[] = ["bed", "silence"];
const DEEP_ROLES: SoundRole[] = ["bed", "ambience", "silence"];
const STING_ROLES: SoundRole[] = ["bed", "ambience", "sting", "silence"];

type SoundTab = "music" | "sfx";

export function SoundTool({
  session,
  playheadSourceTick,
}: {
  session: DeskSession;
  /** Current SOURCE-footage tick under the playhead — where a dropped sound lands. */
  playheadSourceTick?: number;
}) {
  const { pack, packId, applyOp, resolved } = session;
  const store = useStudioStore();
  const deep = Boolean(session.soundDeep);
  const stingPack = Boolean(session.soundStingPack);
  const allAssets = useMemo(() => packAudioAssets(pack), [pack]);
  const audioAssets = useMemo(
    () => (stingPack ? allAssets : allAssets.filter((a) => !isStingLikeAsset(a))),
    [allAssets, stingPack],
  );
  const roles = stingPack ? STING_ROLES : deep ? DEEP_ROLES : BASIC_ROLES;
  const sourceEnd = packEndTick(pack);
  const tickRate = pack.tickRate || 30;
  const title = dockLabelFor("sound", packId);
  const auditionRef = useRef(new AudioAuditionController());

  useEffect(() => {
    const ctrl = auditionRef.current;
    return () => ctrl.dispose();
  }, []);

  const softAsset =
    audioAssets.find((a) => a.id === WARM_AUDIO_ASSET_ID || a.tags.includes("soft")) ??
    audioAssets[0];

  const sfxOptions = useMemo(
    () => (pack.sfxOptions ?? []).filter((o) => Boolean(o.assetId)),
    [pack.sfxOptions],
  );

  const effective = effectiveSound(resolved);
  const activeMood: MusicMoodId | null = useMemo(
    () =>
      musicMoodFromResolved(resolved, {
        warmAssetId: softAsset?.id ?? WARM_AUDIO_ASSET_ID,
        assetTags: (id) => pack.assets.find((a) => a.id === id)?.tags ?? [],
      }),
    [resolved, softAsset?.id, pack.assets],
  );

  const [tab, setTab] = useState<SoundTab>("music");
  const [assetId, setAssetId] = useState(audioAssets[0]?.id ?? WARM_AUDIO_ASSET_ID);
  const [role, setRole] = useState<SoundRole>("bed");
  const [startTick, setStartTick] = useState(0);
  const [endTick, setEndTick] = useState(sourceEnd || 1);
  const [volumeMilli, setVolumeMilli] = useState(600);
  // Which placed SFX instance is selected for editing (unlimited list; no slots).
  const [selectedSfxId, setSelectedSfxId] = useState<string | null>(null);

  useEffect(() => {
    setTab("music");
    setSelectedSfxId(null);
    // Reset section only when the pack identity changes — not on every resolved edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pack follows packId
  }, [packId]);

  useEffect(() => {
    if (!effective) {
      setAssetId(softAsset?.id ?? WARM_AUDIO_ASSET_ID);
      setRole("silence");
      setStartTick(0);
      setEndTick(packEndTick(pack) || 1);
      setVolumeMilli(0);
      return;
    }
    setAssetId(effective.assetId);
    setRole(effective.role);
    setStartTick(effective.startTick);
    setEndTick(effective.endTick);
    setVolumeMilli(effective.volumeMilli);
  }, [
    effective?.opId,
    effective?.assetId,
    effective?.role,
    effective?.startTick,
    effective?.endTick,
    effective?.volumeMilli,
    softAsset?.id,
    pack,
  ]);

  useEffect(() => {
    if (!roles.includes(role)) setRole("bed");
  }, [roles, role]);

  useEffect(() => {
    if (!audioAssets.some((a) => a.id === assetId)) {
      setAssetId(audioAssets[0]?.id ?? WARM_AUDIO_ASSET_ID);
    }
  }, [audioAssets, assetId]);

  // Placed one-shot SFX, ordered by their moment for a readable list.
  const placedSfx = useMemo(
    () => [...resolved.sfx].sort((a, b) => a.atTick - b.atTick),
    [resolved.sfx],
  );
  // Owned-only SFX cards, keeping any asset already placed (unowned loaded graph).
  const placedAssetIds = useMemo(
    () => new Set(resolved.sfx.map((s) => s.assetId)),
    [resolved.sfx],
  );
  const visibleSfxOptions = sfxOptions.filter(
    (o) => store.isOptionOwned("sfx", o.id) || (o.assetId ? placedAssetIds.has(o.assetId) : false),
  );

  const dropTick = () =>
    Math.max(0, Math.min(playheadSourceTick ?? 0, Math.max(0, sourceEnd - 1)));

  const previewAsset = (id: string) => {
    try {
      const url = playableAudioUrl(pack, id);
      auditionRef.current.play(url, { muted: prefersReducedMotion(), volume: 0.55 });
    } catch {
      /* missing asset path — ignore */
    }
  };

  const applySound = (next: {
    assetId: string;
    role: SoundRole;
    startTick: number;
    endTick: number;
    volumeMilli: number;
  }) => {
    applyOp({
      kind: "sound",
      opId: nextOpId("sound"),
      assetId: next.assetId,
      role: next.role,
      startTick: next.startTick,
      endTick: next.endTick,
      volumeMilli: next.role === "silence" ? 0 : next.volumeMilli,
    });
  };

  const applyMood = (id: MusicMoodId) => {
    const end = Math.max(1, sourceEnd || 1);
    const def = MUSIC_MOOD_DEFS.find((m) => m.id === id)!;
    if (id === "clean") {
      const asset = softAsset?.id ?? WARM_AUDIO_ASSET_ID;
      applySound({
        assetId: asset,
        role: "silence",
        startTick: 0,
        endTick: end,
        volumeMilli: 0,
      });
      return;
    }
    const asset = def.assetId ?? softAsset?.id ?? WARM_AUDIO_ASSET_ID;
    applySound({
      assetId: asset,
      role: "bed",
      startTick: 0,
      endTick: end,
      volumeMilli: 700,
    });
    previewAsset(asset);
  };

  const applyFineMusic = () => {
    const end = deep
      ? Math.max(startTick + 1, Math.min(endTick, sourceEnd))
      : Math.max(1, sourceEnd || 1);
    const start = deep ? Math.max(0, Math.min(startTick, end - 1)) : 0;
    const nextRole: SoundRole =
      role === "sting" && !stingPack ? "bed" : roles.includes(role) ? role : "bed";
    applySound({
      assetId,
      role: nextRole,
      startTick: start,
      endTick: end,
      volumeMilli: nextRole === "silence" ? 0 : volumeMilli,
    });
  };
  void applyFineMusic;

  // Drop a NEW sound instance at the playhead (mint a fresh id; unlimited).
  const dropSfx = (optionAssetId: string, volume = 700) => {
    const id = nextSfxInstanceId(session.graph);
    applyOp({
      kind: "sfx",
      opId: nextOpId("sfx"),
      id,
      assetId: optionAssetId,
      atTick: dropTick(),
      volumeMilli: volume,
    });
    setSelectedSfxId(id);
    previewAsset(optionAssetId);
  };

  // Re-emit an existing instance (same id → last-wins) at a new moment.
  const reemitSfx = (
    inst: { id: string; assetId: string; volumeMilli: number },
    patch: { atTick: number },
  ) => {
    applyOp({
      kind: "sfx",
      opId: nextOpId("sfx"),
      id: inst.id,
      assetId: inst.assetId,
      atTick: patch.atTick,
      volumeMilli: inst.volumeMilli,
    });
  };

  const deleteSfx = (id: string) => {
    applyOp({
      kind: "sfx",
      opId: nextOpId("sfx-clear"),
      id,
      assetId: null,
      atTick: 0,
      volumeMilli: 0,
    });
    if (selectedSfxId === id) setSelectedSfxId(null);
    auditionRef.current.stop();
  };

  const sfxLabelFor = (assetId: string) =>
    sfxOptions.find((o) => o.assetId === assetId)?.label ?? assetId;

  return (
    <div className="tool-panel kid-tool" data-testid="sound-tool">
      <ToolCartButton tool="sound" />
      <h3>{title}</h3>
      <p className="coach-line" data-testid="coach-line">
        {coachLineFor("sound")}
      </p>

      <div className="tool-subtabs" role="group" aria-label="Sound sections" data-testid="sound-tabs">
        {(
          [
            ["music", "Music"],
            ["sfx", "SFX"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`tool-subtab${tab === id ? " active" : ""}`}
            data-testid={`sound-tab-${id}`}
            aria-pressed={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "music" && (
        <div data-testid="sound-music-panel">
          <div
            className="treatment-grid kid-treatments"
            data-testid="sound-treatments"
            role="group"
            aria-label="Music mood"
          >
            {MUSIC_MOOD_DEFS.map((mood) => (
              <button
                key={mood.id}
                type="button"
                className={`treatment treatment-card${activeMood === mood.id ? " active" : ""}`}
                data-testid={`sound-treatment-${mood.testId}`}
                aria-pressed={activeMood === mood.id}
                onClick={() => applyMood(mood.id)}
              >
                <strong>{mood.label}</strong>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "sfx" && (
        <div data-testid="sound-sfx-panel">
          <p className="coach-line-sub" data-testid="sfx-hint">
            Scrub the video, then tap a sound to drop it at the playhead. Add as many as you like.
          </p>

          <div className="treatment-grid kid-treatments" data-testid="sfx-options">
            {visibleSfxOptions.map((opt) => (
              <CreativeOptionCard
                key={opt.id}
                kind="sfx"
                optionId={opt.id}
                label={opt.label}
                testId={`sfx-card-${opt.id}`}
                selected={false}
                disabled={!opt.assetId}
                onApply={() => {
                  if (!opt.assetId) return;
                  dropSfx(opt.assetId, opt.defaultVolumeMilli ?? 700);
                }}
                onPreview={() => {
                  if (!opt.assetId) return;
                  const asset = pack.assets.find((a) => a.id === opt.assetId);
                  if (!asset || asset.kind !== "audio") return;
                  try {
                    auditionRef.current.play(playableAudioUrl(pack, opt.assetId), {
                      volume: (opt.defaultVolumeMilli ?? 700) / 1000,
                      muted: prefersReducedMotion(),
                    });
                  } catch {
                    /* missing audio */
                  }
                }}
              />
            ))}
          </div>

          <div className="sfx-placed" data-testid="sfx-placed">
            {placedSfx.length === 0 ? (
              <p className="empty" data-testid="sfx-empty">
                No sounds yet. Scrub the video and tap a sound to drop it.
              </p>
            ) : (
              placedSfx.map((inst) => (
                <div
                  key={inst.id}
                  className={`sfx-placed-row${selectedSfxId === inst.id ? " active" : ""}`}
                  data-testid={`sfx-instance-${inst.id}`}
                >
                  <button
                    type="button"
                    className="sfx-placed-select"
                    data-testid={`sfx-select-${inst.id}`}
                    aria-pressed={selectedSfxId === inst.id}
                    onClick={() => {
                      setSelectedSfxId(inst.id);
                      previewAsset(inst.assetId);
                    }}
                  >
                    <span className="sfx-placed-label">{sfxLabelFor(inst.assetId)}</span>
                    <span className="sfx-placed-time">
                      {(inst.atTick / tickRate).toFixed(1)}s
                    </span>
                  </button>
                  <button
                    type="button"
                    className="sfx-placed-retime"
                    data-testid={`sfx-retime-${inst.id}`}
                    aria-label={`Move ${sfxLabelFor(inst.assetId)} to the playhead (${(dropTick() / tickRate).toFixed(1)}s)`}
                    title={`Move to playhead (${(dropTick() / tickRate).toFixed(1)}s)`}
                    onClick={() => reemitSfx(inst, { atTick: dropTick() })}
                  >
                    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden focusable="false">
                      <path d="M5 3.5 H11 L8 7 Z" fill="currentColor" />
                      <line
                        x1="8"
                        y1="6.5"
                        x2="8"
                        y2="12"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                      <line
                        x1="2.5"
                        y1="12"
                        x2="13.5"
                        y2="12"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="sfx-placed-delete"
                    data-testid={`sfx-delete-${inst.id}`}
                    aria-label={`Delete ${sfxLabelFor(inst.assetId)}`}
                    onClick={() => deleteSfx(inst.id)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
